import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VoicemailIcon from '@mui/icons-material/Voicemail';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import { voicemailApi } from '../../services/api';
import { MessageAttachment } from '../../types';

const SPEEDS = [1, 1.5, 2];
const POLL_MS = 20000;
const POLL_TRIES = 15;
const AUDIO_NAME = /\.(mp3|amr|m4a|wav|ogg|aac|3gp)$/i;

type Transcript = { status: string; source: string | null; text: string };

/** One voicemail plays at a time across the page. */
let playing: HTMLAudioElement | null = null;

export const isVoicemailAttachment = (a?: MessageAttachment | null, messageId?: string | null): boolean =>
  !!a && !!a.id && !!messageId && a.source !== 'response' &&
  (String(a.contentType || '').toLowerCase().startsWith('audio') || AUDIO_NAME.test(a.filename || ''));

const clock = (s: number) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

const errText = async (e: any, fallback: string): Promise<string> => {
  const d = e?.response?.data;
  if (d instanceof Blob) {
    try { return JSON.parse(await d.text()).message || fallback; } catch { return fallback; }
  }
  return (d && typeof d === 'object' && d.message) || fallback;
};

const fromAttachment = (a: MessageAttachment): Transcript => ({
  status: a.transcriptStatus || 'pending',
  source: a.transcriptSource || null,
  text: a.transcriptStatus === 'completed' && typeof a.transcription === 'string' ? a.transcription : '',
});

const fromApi = (d: any): Transcript => ({ status: d.status, source: d.source || null, text: d.text || '' });

const STATUS_TEXT: Record<string, string> = {
  pending: 'Transcribing…',
  no_speech: 'No words could be heard in this voicemail.',
  failed: 'The transcript could not be made. Use Transcribe again.',
  no_audio: 'The recording for this voicemail is not available.',
};

const SOURCE_TEXT: Record<string, string> = {
  spruce: 'Transcript from Spruce',
  levitan_ai: 'Transcript by Levitan AI',
};

type Props = {
  messageId: string;
  attachment: MessageAttachment;
  compact?: boolean;
  onDownload?: () => void;
};

/** A Spruce voicemail: play it, and read what was said. */
const VoicemailPlayer: React.FC<Props> = ({ messageId, attachment, compact, onDownload }) => {
  const { enqueueSnackbar } = useSnackbar();
  const attachmentId = attachment.id as string;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [transcript, setTranscript] = useState<Transcript>(() => fromAttachment(attachment));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTranscript(fromAttachment(attachment));
  }, [attachment.transcriptStatus, attachment.transcriptSource, attachment.transcription]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      if (playing === a) playing = null;
    }
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  useEffect(() => {
    if (transcript.status !== 'pending') return undefined;
    let alive = true;
    let tries = 0;
    const check = async () => {
      try {
        const r = await voicemailApi.get(messageId, attachmentId);
        if (alive && r.data?.data) setTranscript(fromApi(r.data.data));
      } catch { /* keep waiting */ }
    };
    check();
    const id = window.setInterval(() => {
      tries += 1;
      if (tries > POLL_TRIES) {
        window.clearInterval(id);
        return;
      }
      check();
    }, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [transcript.status, messageId, attachmentId]);

  const loadAudio = async (): Promise<HTMLAudioElement> => {
    if (audioRef.current) return audioRef.current;
    const r = await voicemailApi.audio(messageId, attachmentId);
    const url = URL.createObjectURL(r.data as Blob);
    urlRef.current = url;
    const a = new Audio(url);
    a.onloadedmetadata = () => setDuration(Number.isFinite(a.duration) ? a.duration : 0);
    a.ontimeupdate = () => setTime(a.currentTime);
    a.onplay = () => setIsPlaying(true);
    a.onpause = () => setIsPlaying(false);
    a.onended = () => {
      setIsPlaying(false);
      setTime(0);
    };
    audioRef.current = a;
    return a;
  };

  const toggle = async () => {
    const current = audioRef.current;
    if (current && !current.paused) {
      current.pause();
      return;
    }
    setLoading(true);
    try {
      const a = await loadAudio();
      if (playing && playing !== a) playing.pause();
      playing = a;
      a.playbackRate = speed;
      await a.play();
    } catch (e: any) {
      enqueueSnackbar(await errText(e, 'The voicemail could not be played. Please try Download.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const nextSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const seek = (_: Event, value: number | number[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    if (audioRef.current) audioRef.current.currentTime = v;
    setTime(v);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(transcript.text);
      enqueueSnackbar('Transcript copied.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Copying is not allowed in this browser. Select the text instead.', { variant: 'warning' });
    }
  };

  const transcribeAgain = async () => {
    setBusy(true);
    try {
      const r = await voicemailApi.transcribe(messageId, attachmentId);
      const d = r.data?.data;
      if (d) setTranscript(fromApi(d));
      enqueueSnackbar(d?.status === 'no_speech' ? 'No words could be heard in this voicemail.' : 'Transcript updated.', { variant: 'success' });
    } catch (e: any) {
      const d = e?.response?.data?.data;
      if (d) setTranscript(fromApi(d));
      enqueueSnackbar(await errText(e, 'Transcription failed. Please try again.'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const hasText = transcript.status === 'completed' && !!transcript.text;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 600,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        color: 'text.primary',
        p: compact ? 1 : 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={isPlaying ? 'Pause' : 'Play the voicemail'} arrow>
          <span>
            <IconButton
              color="primary"
              size={compact ? 'small' : 'medium'}
              onClick={toggle}
              disabled={loading || transcript.status === 'no_audio'}
              aria-label={isPlaying ? 'Pause voicemail' : 'Play voicemail'}
              sx={{ border: 1, borderColor: 'primary.light' }}
            >
              {loading ? <CircularProgress size={20} /> : isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <VoicemailIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Voicemail</Typography>
            <Typography variant="caption" color="text.secondary">
              {clock(time)} / {duration ? clock(duration) : '–:––'}
            </Typography>
          </Box>
          <Slider
            size="small"
            value={Math.min(time, duration || 0)}
            max={duration || 1}
            step={0.1}
            disabled={!duration}
            onChange={seek}
            aria-label="Position in voicemail"
            sx={{ py: 0.5 }}
          />
        </Box>
        <Tooltip title="Playback speed" arrow>
          <Button size="small" onClick={nextSpeed} sx={{ minWidth: 44 }}>{speed}×</Button>
        </Tooltip>
        {onDownload && (
          <Tooltip title="Download the recording" arrow>
            <IconButton size="small" onClick={onDownload} aria-label="Download voicemail">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ mt: 1 }}>
        {hasText ? (
          <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1, maxHeight: 220, overflowY: 'auto' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{transcript.text}</Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {transcript.status === 'pending' && <CircularProgress size={12} />}
            {STATUS_TEXT[transcript.status] || STATUS_TEXT.pending}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {hasText || transcript.status === 'no_speech' ? SOURCE_TEXT[transcript.source || ''] || '' : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {hasText && (
              <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copy}>
                Copy text
              </Button>
            )}
            <Tooltip title="Make a new transcript with our own Whisper model" arrow>
              <span>
                <Button
                  size="small"
                  startIcon={busy ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                  onClick={transcribeAgain}
                  disabled={busy || transcript.status === 'no_audio'}
                >
                  {busy ? 'Transcribing…' : 'Transcribe again'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VoicemailPlayer;
